import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Fade,
  Avatar,
  useTheme,
  useMediaQuery,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Stack,
  Snackbar,
} from '@mui/material';
import {
  RoomService,
  AccessTime,
  Star,
  ExpandMore,
  Spa,
  ContentCut,
  AutoAwesome,
  ColorLens,
  Face,
  Brush,
  Share as ShareIcon,
  Print as PrintIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { serviceService, Service } from '../services/serviceService';
import { apiClient } from '../services/api';
import { buildWhatsAppHref } from '../components/common/WhatsAppLink';

interface PublicSalonInfo {
  salonId: string;
  name: string;
  phone: string;
  tagline?: string;
}

const CATEGORY_IMAGES: Record<string, string> = {
  'Facial': '/images/facial.png',
  'Manicure': '/images/manicure.png',
  'Pedicure': '/images/manicure.png',
  'D-Tan': '/images/facial.png',
  'Bleach': '/images/facial.png',
  'Threading': '/images/threading.png',
  'Hair Cuts': '/images/hair-cuts.png',
  'Hair Treatments': '/images/hair-treatments.png',
  'Advanced Hair Treatments': '/images/hair-treatments.png',
  'Hair Color': '/images/hair-cuts.png',
};

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  'Facial': <Face />,
  'Manicure': <Brush />,
  'Pedicure': <Spa />,
  'Threading': <AutoAwesome />,
  'Hair Cuts': <ContentCut />,
  'Hair Color': <ColorLens />,
};

const PublicMenuPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [services, setServices] = useState<Service[]>([]);
  const [salon, setSalon] = useState<PublicSalonInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | false>('Facial');
  const [snack, setSnack] = useState<string | null>(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const [svcResp, salonResp] = await Promise.all([
          serviceService.getPublicServices(),
          apiClient
            .get<{ data?: PublicSalonInfo }>('/salon/public')
            .catch(() => null), // salon info is best-effort; the menu still renders without it
        ]);
        setServices(svcResp.services);
        if (salonResp?.data) setSalon(salonResp.data);

        const categories = [...new Set(svcResp.services.map((s) => s.category))];
        if (categories.length > 0 && !categories.includes('Facial')) {
          setExpanded(categories[0]);
        }
      } catch (err) {
        console.error('Failed to fetch services:', err);
        setError('Failed to load menu. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const handleShare = async () => {
    const url = window.location.href;
    const title = salon ? `${salon.name} — Service Menu` : 'Service Menu';
    const nav = window.navigator as Navigator & {
      share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
    };
    if (nav.share) {
      try {
        await nav.share({ title, url });
        return;
      } catch {
        // user cancelled — fall through to clipboard
      }
    }
    try {
      await window.navigator.clipboard.writeText(url);
      setSnack('Link copied to clipboard');
    } catch {
      setSnack('Could not share — copy the URL from your address bar');
    }
  };

  const handlePrint = () => window.print();

  const whatsAppHref = salon?.phone
    ? buildWhatsAppHref(
        salon.phone,
        `Hi ${salon.name}, I'd like to book an appointment.`
      )
    : '';

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const categories = [...new Set(services.map((s) => s.category))];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#fdf6f6' }}>
        <CircularProgress sx={{ color: '#E8B4B8' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: '#fdf6f6', pb: 10 }}>
      {/* Hero Header */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '250px', md: '350px' },
          background: 'linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.4)), url("/images/cover.png") center/cover',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          color: 'white',
          borderRadius: '0 0 40px 40px',
          mb: 4,
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        }}
      >
        <Fade in timeout={1000}>
          <Box
            sx={{
              position: 'absolute',
              bottom: '30px',
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              px: 4,
              py: 1.5,
              borderRadius: '20px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
              textAlign: 'center',
              width: 'max-content',
            }}
          >
            <Typography
              variant={isMobile ? 'h5' : 'h4'}
              sx={{
                fontWeight: 900,
                color: 'white',
                letterSpacing: '0.15em',
                textShadow: '0 2px 8px rgba(0,0,0,0.2)',
                textTransform: 'uppercase',
              }}
            >
              Service Menu
            </Typography>
          </Box>
        </Fade>
      </Box>

      <Container maxWidth="md">
        {/* Share + Print + Book CTA. Hidden when printing. */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          justifyContent="center"
          sx={{ mb: 3, '@media print': { display: 'none' } }}
          className="no-print"
        >
          <Button
            variant="outlined"
            startIcon={<ShareIcon />}
            onClick={() => void handleShare()}
            sx={{
              borderColor: '#E8B4B8',
              color: '#5C2A30',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { borderColor: '#D69BA0', bgcolor: 'rgba(232,180,184,0.08)' },
            }}
          >
            Share menu
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{
              borderColor: '#E8B4B8',
              color: '#5C2A30',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { borderColor: '#D69BA0', bgcolor: 'rgba(232,180,184,0.08)' },
            }}
          >
            Print / Save PDF
          </Button>
          {whatsAppHref && (
            <Button
              variant="contained"
              startIcon={<WhatsAppIcon />}
              component="a"
              href={whatsAppHref}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                bgcolor: '#25D366',
                color: 'white',
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: '0 4px 14px rgba(37,211,102,0.35)',
                '&:hover': { bgcolor: '#1FB957' },
              }}
            >
              Book via WhatsApp
            </Button>
          )}
        </Stack>

        {error ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 4, boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
            <Typography color="error">{error}</Typography>
          </Paper>
        ) : (
          categories.map((category) => (
            <Accordion
              key={category}
              expanded={expanded === category}
              onChange={handleChange(category)}
              sx={{
                mb: 2,
                borderRadius: '16px !important',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                '&:before': { display: 'none' },
                border: '1px solid rgba(232, 180, 184, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(232, 180, 184, 0.15)',
                  borderColor: 'rgba(232, 180, 184, 0.3)',
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: '#E8B4B8' }} />}
                sx={{
                  px: 3,
                  py: 1,
                  background: expanded === category ? 'rgba(232, 180, 184, 0.05)' : 'white',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: expanded === category ? '#E8B4B8' : '#F5D5D8', 
                      color: expanded === category ? 'white' : '#E8B4B8',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {CATEGORY_ICONS[category] || <Spa />}
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#2D3748' }}>
                    {category}
                  </Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 0 }}>
                {/* Category Cover Image */}
                <Box 
                  sx={{ 
                    height: { xs: '200px', md: '260px' }, 
                    width: '100%',
                    background: `url("${CATEGORY_IMAGES[category] || '/images/facial.png'}") center/cover`,
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                />

                <Box sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    {services
                      .filter((s) => s.category === category)
                      .map((service, index) => (
                        <Grid item xs={12} key={service.id}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 3,
                              bgcolor: 'white',
                              border: '1px solid #f1f1f1',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: '#fafafa',
                                transform: 'scale(1.01)',
                                borderColor: '#E8B4B8'
                              }
                            }}
                          >
                            <Box sx={{ flexGrow: 1, pr: 2 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2D3748' }}>
                                {service.name}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <AccessTime sx={{ fontSize: 14, color: '#A0AEC0' }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {service.duration} mins
                                </Typography>
                              </Box>
                            </Box>
                            
                            <Box sx={{ textAlign: 'right', minWidth: '80px' }}>
                              {service.discountPrice ? (
                                <>
                                  <Typography variant="caption" sx={{ textDecoration: 'line-through', color: '#A0AEC0', display: 'block' }}>
                                    ₹{service.price}
                                  </Typography>
                                  <Typography variant="h6" sx={{ color: '#E8B4B8', fontWeight: 800 }}>
                                    ₹{service.discountPrice}
                                  </Typography>
                                </>
                              ) : (
                                <Typography variant="h6" sx={{ color: '#2D3748', fontWeight: 800 }}>
                                  ₹{service.price}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                  </Grid>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Container>

      {/* Footer Branding */}
      <Box sx={{ textAlign: 'center', mt: 8, px: 2 }}>
        <Divider sx={{ mb: 4, maxWidth: '200px', mx: 'auto' }} />
        <Typography variant="body2" sx={{ color: '#A0AEC0', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          {salon?.name || 'Preeti Beauty & Makeup Studio'}
        </Typography>
        <Typography variant="caption" sx={{ color: '#CBD5E0', mt: 1, display: 'block' }}>
          Crafted with love for an exquisite experience
        </Typography>
      </Box>

      {/* Floating WhatsApp CTA — sticky on mobile so booking is one tap away. */}
      {whatsAppHref && (
        <Box
          sx={{
            position: 'fixed',
            bottom: { xs: 20, md: 32 },
            right: { xs: 20, md: 32 },
            zIndex: 1200,
            '@media print': { display: 'none' },
          }}
        >
          <Button
            variant="contained"
            startIcon={<WhatsAppIcon />}
            component="a"
            href={whatsAppHref}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              bgcolor: '#25D366',
              color: 'white',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 999,
              px: 3,
              py: 1.5,
              boxShadow: '0 8px 24px rgba(37,211,102,0.45)',
              '&:hover': { bgcolor: '#1FB957' },
            }}
          >
            Book on WhatsApp
          </Button>
        </Box>
      )}

      <Snackbar
        open={!!snack}
        autoHideDuration={2500}
        onClose={() => setSnack(null)}
        message={snack ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default PublicMenuPage;
